// Game::GetVar / Game::SetVar — game/game.cpp:2675-2747. The trigger system never
// reads the pvar dictionary directly: every read goes through GetVar, which resolves
// '$'-prefixed SYSTEM variables BEFORE consulting the dictionary (and the dictionary
// Get itself relinks — see data/dictionary.ts). SetVar asserts no '$' (sysvars are
// read-only).

import type { SimContext } from '../context.ts';
import { kfHcapEasy, kfHcapNormal, kfHcapHard, kfHcapDefault } from '../player/side.ts';

/** Returns the value, or null when the C++ returns false ("?var?"/"?pvar?"). */
export function GetGameVar(ctx: SimContext, name: string): string | null {
  if (name.charCodeAt(0) === 0x24 /* '$' */) {
    const lower = name.toLowerCase(); // C++ stricmp
    switch (lower) {
      case '$ranktitle':
        // game.cpp:2680 GetRankTitle — a word from the rank-title string table.
        // Titles are non-numeric, so for TestPvar (atoi) semantics '' is equivalent;
        // textual {$ranktitle} expansion is host/presentation, not sim.
        return '';
      case '$grayscale':
        return '0'; // gfGrayscale — display pref; false on the web client
      case '$iphone':
        // Was a compile-time constant (!defined(IPHONE)); now reflects the LOCAL
        // player's active UI so $iphone-gated tutorial ecoms (S_01) pick the
        // touch (multitouch) vs mouse (single-pointer) variant. ctx.fTouchControls
        // is mirrored from gfMouseMode by the client (SimUIForm.SetUIType).
        return ctx.fTouchControls ? '1' : '0';
      case '$difficulty': {
        // game.cpp:2693-2708 — switch on gwfHandicap (the LOCAL player's handicap;
        // PlayerMgr load/options keep the global in step with gpplrLocal)
        const wf = ctx.plrLocal !== null ? ctx.plrLocal.GetHandicap() : kfHcapDefault;
        if (wf === kfHcapEasy) return '1';
        if (wf === kfHcapNormal) return '2';
        if (wf === kfHcapHard) return '3';
        return '0';
      }
      case '$demo':
        return '0'; // gfDemo — false on the web full version
      default:
        return null; // "?var?" + false
    }
  }
  return ctx.pvars.Get(name); // relinks at head (move-to-front)
}

/** game.cpp:2741-2747 — sysvars are read-only. */
export function SetGameVar(ctx: SimContext, name: string, value: string): boolean {
  // Assert(*pszName != '$') in C++ — shipped levels never do it; keep the contract.
  return ctx.pvars.Set(name, value);
}
